/**
 * DeleteVolumeModal Component
 *
 * Modal for confirming volume deletion with code verification.
 */

import React, { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { useDeleteVolume } from '../hooks/useDeleteVolume';
import { useAuth } from '../../../contexts/AuthContext';
import type { Volume } from '../../../types/volume.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface DeleteVolumeModalProps {
  isOpen: boolean;
  onClose: () => void;
  volume: Volume;
  onSuccess?: () => void;
}

/**
 * DeleteVolumeModal component
 */
export const DeleteVolumeModal: React.FC<DeleteVolumeModalProps> = ({
  isOpen,
  onClose,
  volume,
  onSuccess,
}) => {
  const { tenant } = useAuth();
  const tenantId = tenant?.tenantId || '';
  const [confirmationCode, setConfirmationCode] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Delete mutation
  const deleteMutation = useDeleteVolume(tenantId, {
    onSuccess: () => {
      setConfirmationCode('');
      setErrorMessage('');
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      setErrorMessage(error);
    },
  });

  const handleClose = () => {
    if (!deleteMutation.isPending) {
      setConfirmationCode('');
      setErrorMessage('');
      onClose();
    }
  };

  const handleDelete = () => {
    if (confirmationCode === volume.volumeCode) {
      setErrorMessage('');
      deleteMutation.mutate(volume.volumeId);
    }
  };

  const isCodeValid = confirmationCode === volume.volumeCode;
  const accessGrantsCount = volume.accessGrants?.length || 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>
        <span className="text-red-600">Delete Volume</span>
      </Modal.Header>

      <Modal.Body>
        <div className="space-y-4">
          {/* Error Alert */}
          {errorMessage && (
            <Alert variant="error">
              {errorMessage}
            </Alert>
          )}

          {/* Warning about access grants */}
          {accessGrantsCount > 0 && (
            <Alert variant="error">
              <p className="font-semibold">Cannot delete volume with active access grants!</p>
              <p className="text-sm mt-1">
                This volume has {accessGrantsCount} active access grant{accessGrantsCount !== 1 ? 's' : ''}.
                Please revoke all access grants before deleting.
              </p>
            </Alert>
          )}

          {/* Warning */}
          <Alert variant="warning">
            <div className="space-y-2">
              <p className="font-semibold">This action cannot be undone!</p>
              <p className="text-sm">
                Deleting this volume will permanently remove:
              </p>
              <ul className="text-sm list-disc list-inside ml-2">
                <li>The volume configuration and metadata</li>
                <li>All stored files and objects ({volume.objectCount || 0} objects)</li>
                <li>All access grants and permissions</li>
              </ul>
            </div>
          </Alert>

          {/* Volume Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div>
              <span className="text-sm font-medium text-gray-700">Volume Name:</span>
              <p className="text-gray-900 font-semibold">{volume.volumeName}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Volume Code:</span>
              <p className="text-gray-900 font-mono bg-gray-200 px-2 py-1 rounded inline-block mt-1">
                {volume.volumeCode}
              </p>
            </div>
            {volume.totalSizeBytes !== undefined && (
              <div>
                <span className="text-sm font-medium text-gray-700">Total Size:</span>
                <p className="text-gray-900">{formatBytes(volume.totalSizeBytes)}</p>
              </div>
            )}
          </div>

          {/* Confirmation Input */}
          <div>
            <Input
              label={
                <span>
                  Type <code className="bg-gray-200 px-1 rounded font-mono text-sm">{volume.volumeCode}</code> to confirm
                </span>
              }
              placeholder="Enter volume code"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              disabled={deleteMutation.isPending || accessGrantsCount > 0}
              autoComplete="off"
            />
            {confirmationCode && !isCodeValid && (
              <p className="mt-1 text-sm text-red-600">
                Volume code does not match
              </p>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={!isCodeValid || deleteMutation.isPending || accessGrantsCount > 0}
            loading={deleteMutation.isPending}
          >
            Delete Volume
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Format bytes to human-readable string
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
